import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import type { ResetPasswordRequest, User } from '@/types/api';

function validatePassword(pw: string): string | undefined {
  if (!pw) return 'Password is required.';
  if (pw.length < 12) return 'Must be at least 12 characters.';
  if (!/[A-Z]/.test(pw)) return 'Must contain an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Must contain a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Must contain a number.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Must contain a special character.';
  return undefined;
}

export function ResetPasswordModal({
  user,
  onClose,
  onSave,
}: {
  user: User;
  onClose: () => void;
  onSave: (data: ResetPasswordRequest) => Promise<void>;
}) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function validate(): boolean {
    const newErrors: typeof errors = {};
    const pwError = validatePassword(newPassword);
    if (pwError) newErrors.newPassword = pwError;
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm the password.';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      await onSave({ newPassword });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message ?? 'Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Reset Password"
      description={`Set a new password for ${user.firstName} ${user.lastName}.`}
      size="sm"
    >
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="New Password"
          type="password"
          required
          value={newPassword}
          onChange={e => {
            setNewPassword(e.target.value);
            setErrors(p => ({ ...p, newPassword: undefined }));
          }}
          helperText="12+ characters with uppercase, lowercase, number, and special character."
          error={errors.newPassword}
        />
        <Input
          label="Confirm Password"
          type="password"
          required
          value={confirmPassword}
          onChange={e => {
            setConfirmPassword(e.target.value);
            setErrors(p => ({ ...p, confirmPassword: undefined }));
          }}
          error={errors.confirmPassword}
        />

        {submitError && <p className="text-sm text-error-600">{submitError}</p>}

        <div className="flex gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} className="flex-1">
            Reset Password
          </Button>
        </div>
      </form>
    </Modal>
  );
}
